import { useEffect, useRef, useState } from 'react';
import {
  AFKOPPELBESLUITEN,
  CONTACT_SOORTEN,
  EIGENDOMSVORMEN,
  LAAG_TOETS,
  LAAG_UITLEG,
  MAATREGELENBESLUITEN,
  REALISATIE_STATUSSEN,
  SUBSIDIE_STATUSSEN,
  afkoppelStatus,
  heeftInhoudelijkeVoortgang,
  spiegelLaatsteContact,
  totaleBergingM3,
  type Afkoppelbesluit,
  type Eigendom,
  type Maatregelenbesluit,
  type Foto,
  type RealisatieStatus,
  type Contactmoment,
  type ContactSoort,
  type Registratie,
  type SubsidieStatus,
} from '@/types/domain';
import { isHuurwoning } from '@/data/huurwoningen';
import { adresVolledig, datumNL, relatieveTijd, vandaagISO } from '@/utils/format';
import { nieuweUuid } from '@/utils/id';
import { useAutosave } from '@/hooks/useAutosave';
import { draftService } from '@/services/draftService';
import { useApp } from '@/state/AppContext';
import { Keuzeveld, Tekstveld, Tekstvak, Vinkje } from '@/components/ui/Velden';
import { Knop } from '@/components/ui/Knop';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { CameraIcoon, OpslaanIcoon, PrullenbakIcoon } from '@/components/layout/Iconen';
import { Schrijftip } from './Schrijftip';
import { Sectie } from './Sectie';
import { Contactmomenten } from './Contactmomenten';
import { TegelKiezer } from './TegelKiezer';
import { TuinVelden, tuinIngevuld, tuinSamenvatting } from './TuinVelden';
import { SectieTrap, type SectieStand } from './SectieTrap';
import { StatusStip } from '@/components/ui/StatusStip';
import { FotoSectie } from './FotoSectie';
import { Historie } from './Historie';
import { heeftFouten, valideer, type Validatiefouten } from './validatie';
import './form.css';

interface RegistratieFormulierProps {
  registratie: Registratie;
  /** Nog nooit opgeslagen. Dan valt er niets te corrigeren. */
  isNieuw: boolean;
  bijSluiten: () => void;
}

/**
 * Het volledige registratieformulier voor één woning.
 *
 * De formulierstaat is lokaal; pas bij "Opslaan" gaat hij naar de database.
 * Ondertussen bewaart useAutosave elke wijziging als concept.
 */
export function RegistratieFormulier({
  registratie,
  isNieuw,
  bijSluiten,
}: RegistratieFormulierProps): JSX.Element {
  const { opslaan, verwijderen, toonMelding, gebruiker } = useApp();

  /**
   * Begin bij het concept als dat nieuwer is dan wat er is opgeslagen.
   *
   * Een coach klikt tussendoor naar het dashboard of laat de bewoner iets
   * zien in de browser. Dit scherm verdwijnt dan uit het geheugen. Zonder
   * deze regel begon hij daarna met een leeg formulier opnieuw.
   */
  const [formulier, setFormulier] = useState<Registratie>(() => {
    const concept = draftService.haal(registratie.id);
    return concept && concept.opgeslagenOp > registratie.gewijzigdOp
      ? concept.registratie
      : registratie;
  });
  const [fouten, setFouten] = useState<Validatiefouten>({});
  const [bezig, setBezig] = useState(false);
  const [bevestigVerwijderen, setBevestigVerwijderen] = useState(false);
  const [vraagOverContact, setVraagOverContact] = useState(false);
  const [nieuwContactSoort, setNieuwContactSoort] = useState<ContactSoort>('Bezoek aan de deur');
  const [nieuwContactDatum, setNieuwContactDatum] = useState(vandaagISO());

  const laatsteAutosave = useAutosave(formulier, !bezig);

  // Andere woning aangeklikt: formulier opnieuw vullen, opnieuw met
  // voorrang voor een nieuwer concept.
  useEffect(() => {
    const concept = draftService.haal(registratie.id);
    setFormulier(
      concept && concept.opgeslagenOp > registratie.gewijzigdOp
        ? concept.registratie
        : registratie
    );
    setFouten({});
    setBevestigVerwijderen(false);
  }, [registratie]);

  // De autosave wacht anderhalve seconde. Wie sneller wegklikt zou die
  // laatste toetsaanslagen kwijt zijn, dus bij het verlaten van het scherm
  // gaat de huidige stand er direct in.
  const laatsteFormulier = useRef(formulier);
  laatsteFormulier.current = formulier;

  // Na een geslaagde opslag is er niets meer te bewaren. Zonder deze vlag
  // zou het sluiten na het opslaan een concept terugzetten met de stand van
  // vlak vóór het opslaan — inclusief het oude versienummer.
  const isOpgeslagen = useRef(false);

  useEffect(
    () => () => {
      if (isOpgeslagen.current) return;
      draftService.bewaar(laatsteFormulier.current);
    },
    []
  );

  // Naam van de ingelogde coach overnemen zolang het veld nog leeg is.
  useEffect(() => {
    if (gebruiker?.naam && !formulier.coach) {
      setFormulier((huidig) => ({ ...huidig, coach: gebruiker.naam }));
    }
  }, [gebruiker, formulier.coach]);

  const zet = <K extends keyof Registratie>(veld: K, waarde: Registratie[K]) => {
    setFormulier((huidig) => ({ ...huidig, [veld]: waarde }));
  };

  const zetAdres = <K extends keyof Registratie['adres']>(
    veld: K,
    waarde: Registratie['adres'][K]
  ) => {
    setFormulier((huidig) => ({ ...huidig, adres: { ...huidig.adres, [veld]: waarde } }));
  };

  const zetBewoner = <K extends keyof Registratie['bewoner']>(
    veld: K,
    waarde: Registratie['bewoner'][K]
  ) => {
    setFormulier((huidig) => ({ ...huidig, bewoner: { ...huidig.bewoner, [veld]: waarde } }));
  };

  const bewaarDefinitief = async (teBewaren: Registratie) => {
    setBezig(true);
    // De gespiegelde bezoekvelden volgen uit het laatste contactmoment; dat
    // gebeurt hier, vlak voor het wegschrijven, zodat ze nooit achterlopen.
    await opslaan(spiegelLaatsteContact(teBewaren));
    isOpgeslagen.current = true;
    setBezig(false);
    bijSluiten();
  };

  const bewaar = async () => {
    const nieuweFouten = valideer(formulier);
    setFouten(nieuweFouten);

    if (heeftFouten(nieuweFouten)) {
      toonMelding('fout', 'Controleer de gemarkeerde velden.');
      return;
    }

    /**
     * Is er voortgang geboekt zonder dat er contact is vastgelegd?
     *
     * Een besluit dat omslaat komt ergens vandaan. Wie dat wijzigt en geen
     * contactmoment toevoegt, is er waarschijnlijk een vergeten — maar het
     * kan ook een correctie zijn. Alleen in dat geval vragen we het, en
     * verder nooit.
     */
    const zonderNieuwContact =
      formulier.contactmomenten.length === registratie.contactmomenten.length;

    // Bij een eerste registratie is alles nieuw; dan is de vraag onzin.
    if (!isNieuw && zonderNieuwContact && heeftInhoudelijkeVoortgang(registratie, formulier)) {
      setVraagOverContact(true);
      return;
    }

    await bewaarDefinitief(formulier);
  };

  /** "Nee, dit was een correctie": gewoon opslaan, geen contactmoment erbij. */
  const bewaarAlsCorrectie = async () => {
    setVraagOverContact(false);
    await bewaarDefinitief(formulier);
  };

  /**
   * "Ja": soort en datum worden in hetzelfde venster gevraagd en het moment
   * gaat er meteen bij. De coach hoeft daarna niets meer op te zoeken; een
   * toelichting kan hij later in de sectie Contact aanvullen.
   */
  const bewaarMetContact = async () => {
    const moment: Contactmoment = {
      id: nieuweUuid(),
      datum: nieuwContactDatum,
      soort: nieuwContactSoort,
      coach: gebruiker?.naam ?? formulier.coach,
      uitkomst: formulier.uitkomstBezoek,
      toelichting: '',
    };

    const bijgewerkt: Registratie = {
      ...formulier,
      contactmomenten: [...formulier.contactmomenten, moment],
    };

    setFormulier(bijgewerkt);
    setVraagOverContact(false);
    await bewaarDefinitief(bijgewerkt);
  };

  const wisBewoner = () => {
    setFormulier((huidig) => ({
      ...huidig,
      bewoner: {
        naam: '',
        telefoon: '',
        email: '',
        toestemmingPersoonsgegevens: false,
        toelichting: huidig.bewoner.toelichting,
      },
    }));
  };

  const berging = totaleBergingM3(formulier);

  const contactSamenvatting = (registratie: Registratie): string | undefined => {
    const aantal = registratie.contactmomenten.length;
    if (aantal === 0) return undefined;
    const laatste = registratie.contactmomenten[aantal - 1];
    const delen = [
      aantal === 1 ? '1 contactmoment' : `${aantal} contactmomenten`,
      laatste.uitkomst,
      datumNL(laatste.datum),
    ].filter(Boolean);
    return delen.join(' · ');
  };

  /*
   * Of dit pand van de corporatie is. Dat is geen waarneming aan de deur
   * maar een projectgegeven, en het bepaalt welke vraag een coach hier
   * überhaupt krijgt: bij huur is de regenpijp niet van de bewoner.
   */
  const huur = isHuurwoning(formulier.adres);

  /*
   * Bij een koopwoning is sectie 6 pas af als beide vragen beantwoord zijn.
   *
   * Dat is geen strengheid om de strengheid. Zonder deze regel tikt een coach
   * "koppelt niet af" aan, ziet het vinkje groen worden en scrollt door — en
   * dan blijft de tuinvraag leeg terwijl de bewoner er wel iets over gezegd
   * heeft. De laag Extra maatregelen loopt dan vol met grijs dat niets
   * betekent.
   *
   * Bij huur is er maar één vraag, dus telt die alleen.
   */
  const resultaatIngevuld = huur
    ? Boolean(formulier.maatregelenbesluit)
    : Boolean(formulier.afkoppelbesluit) && Boolean(formulier.maatregelenbesluit);

  const resultaatSamenvatting = (registratie: Registratie): string | undefined => {
    const delen = [
      huur ? 'Corporatie koppelt af' : registratie.afkoppelbesluit,
      registratie.maatregelenbesluit,
      registratie.realisatieStatus && registratie.realisatieStatus !== 'Niet gestart'
        ? registratie.realisatieStatus
        : '',
      totaleBergingM3(registratie) ? `${totaleBergingM3(registratie).toFixed(1)} m³` : '',
    ].filter(Boolean);
    return delen.length > 0 ? delen.join(' · ') : undefined;
  };

  const heeftPersoonsgegevens = Boolean(
    formulier.bewoner.naam.trim() ||
      formulier.bewoner.telefoon.trim() ||
      formulier.bewoner.email.trim()
  );

  const sectieStand: SectieStand[] = [
    { nummer: 1, titel: 'Woning', ingevuld: Boolean(formulier.eigendom) },
    {
      nummer: 2,
      titel: 'Contact',
      ingevuld: formulier.contactmomenten.some((moment) => Boolean(moment.uitkomst)),
    },
    { nummer: 3, titel: 'Bewoner', ingevuld: heeftPersoonsgegevens },
    { nummer: 4, titel: 'Voortuin', ingevuld: tuinIngevuld(formulier.voortuin) },
    { nummer: 5, titel: 'Achtertuin', ingevuld: tuinIngevuld(formulier.achtertuin) },
    {
      nummer: 6,
      titel: 'Resultaat',
      ingevuld: resultaatIngevuld,
    },
    { nummer: 7, titel: "Foto's", ingevuld: formulier.fotos.length > 0 },
    { nummer: 8, titel: 'Wijzigingen', ingevuld: formulier.versie > 1 },
  ];



  return (
    <>
      <SectieTrap secties={sectieStand} />

      {/*
        De rechtermarge draagt wat je over deze woning kunt zien en doen: de
        kleur die hij op de kaart krijgt, en de snelste weg naar een foto.
        Beide stonden onderaan het formulier, waar je pas komt na acht secties
        scrollen — terwijl je juist bovenaan wilt weten waar je aan toe bent.
      */}
      <aside className="woningacties" aria-label="Deze woning">
        <StatusStip status={afkoppelStatus(formulier, huur)} formaat={20} label="Kleur op de kaart" />
        <button
          type="button"
          className="woningacties__knop"
          onClick={() => document.getElementById('sectie-7')?.scrollIntoView({ behavior: 'smooth' })}
          title="Naar de foto's"
          aria-label="Naar de foto's"
        >
          <CameraIcoon />
        </button>
      </aside>

      <div className="paneel__inhoud">
        <Sectie
          nummer={1}
          titel="Woning"
          standaardOpen
          ingevuld={Boolean(formulier.eigendom)}
          samenvatting={formulier.eigendom || undefined}
        >
          {formulier.bagId ? (
            <Tekstveld
              label="Adres"
              waarde={adresVolledig(formulier.adres)}
              bijWijziging={() => undefined}
              hint={`BAG-id ${formulier.bagId}`}
              readOnly
            />
          ) : (
            <>
              <p className="sectie__toelichting">
                Handmatig geplaatst punt. Het dichtstbijzijnde adres is alvast ingevuld;
                pas het aan als dat niet klopt.
              </p>
              <div className="veldenrij veldenrij--twee">
                <Tekstveld
                  label="Straat"
                  waarde={formulier.adres.straat}
                  bijWijziging={(waarde) => zetAdres('straat', waarde)}
                />
                <Tekstveld
                  label="Huisnummer"
                  waarde={formulier.adres.huisnummer}
                  bijWijziging={(waarde) => zetAdres('huisnummer', waarde)}
                />
              </div>
              <div className="veldenrij veldenrij--twee">
                <Tekstveld
                  label="Postcode"
                  waarde={formulier.adres.postcode}
                  bijWijziging={(waarde) => zetAdres('postcode', waarde)}
                />
                <Tekstveld
                  label="Plaats"
                  waarde={formulier.adres.plaats}
                  bijWijziging={(waarde) => zetAdres('plaats', waarde)}
                />
              </div>
            </>
          )}
          <TegelKiezer<Eigendom>
            label="Koop of huur"
            waarde={formulier.eigendom}
            opties={EIGENDOMSVORMEN}
            bijWijziging={(waarde) => zet('eigendom', waarde)}
          />
          {formulier.eigendom === 'Huur' && (
            <p className="sectie__toelichting">
              Bij een huurwoning is meestal toestemming van de verhuurder nodig voordat er
              afgekoppeld kan worden. Noteer dat in de toelichting.
            </p>
          )}
        </Sectie>

        <Sectie
          nummer={2}
          titel="Contact"
          standaardOpen
          ingevuld={formulier.contactmomenten.some((moment) => Boolean(moment.uitkomst))}
          samenvatting={contactSamenvatting(formulier)}
        >
          <Contactmomenten
            momenten={formulier.contactmomenten}
            coach={gebruiker?.naam ?? ''}
            openEerste={isNieuw}
            bijWijziging={(bijwerken) =>
              setFormulier((huidig) => ({
                ...huidig,
                contactmomenten: bijwerken(huidig.contactmomenten),
              }))
            }
          />
          {fouten.contact && (
            <p className="veld__fout" role="alert">
              {fouten.contact}
            </p>
          )}
        </Sectie>

        <Sectie
          nummer={3}
          titel="Bewoner"
          ingevuld={heeftPersoonsgegevens}
          samenvatting={
            heeftPersoonsgegevens ? formulier.bewoner.naam || 'Gegevens vastgelegd' : undefined
          }
        >
          <p className="sectie__toelichting">
            Leg persoonsgegevens alleen vast als de bewoner daar toestemming voor geeft. Zonder
            toestemming worden deze velden niet opgeslagen.
          </p>

          <Tekstveld
            label="Naam bewoner"
            waarde={formulier.bewoner.naam}
            bijWijziging={(waarde) => zetBewoner('naam', waarde)}
            autoComplete="name"
          />

          <div className="veldenrij veldenrij--twee">
            <Tekstveld
              label="Telefoon"
              type="tel"
              inputMode="tel"
              waarde={formulier.bewoner.telefoon}
              bijWijziging={(waarde) => zetBewoner('telefoon', waarde)}
              autoComplete="tel"
            />
            <Tekstveld
              label="E-mail"
              type="email"
              inputMode="email"
              waarde={formulier.bewoner.email}
              bijWijziging={(waarde) => zetBewoner('email', waarde)}
              autoComplete="email"
              fout={fouten.email}
            />
          </div>

          {/* Toestemming moet geinformeerd zijn: de bewoner hoort te weten
              waarvoor hij tekent. Deze tekst kan de coach voorlezen of laten
              zien, zodat iedereen aan de deur hetzelfde vertelt. */}
          <details className="uitleg">
            <summary className="uitleg__kop">Wat vertel je de bewoner?</summary>
            <div className="uitleg__inhoud">
              <p>
                &ldquo;Ik noteer uw naam en telefoonnummer alleen om contact te kunnen opnemen
                over het afkoppelen van uw regenwater. Die gegevens gebruiken we nergens anders
                voor en geven we niet door aan de gemeente. U kunt ze altijd laten verwijderen;
                dat kan via mij of via de gemeente.&rdquo;
              </p>
              <p>
                Wil de bewoner dit liever niet, laat het vinkje dan uit. De rest van het
                formulier kun je gewoon invullen: het advies en de foto&apos;s zijn niet aan een
                persoon gekoppeld.
              </p>
            </div>
          </details>

          <Vinkje
            label="De bewoner geeft toestemming voor het vastleggen van persoonsgegevens"
            hint="Verplicht om naam, telefoon of e-mail te bewaren (AVG)."
            aangevinkt={formulier.bewoner.toestemmingPersoonsgegevens}
            bijWijziging={(waarde) => zetBewoner('toestemmingPersoonsgegevens', waarde)}
          />
          {fouten.toestemming && (
            <p className="veld__fout" role="alert">
              {fouten.toestemming}
            </p>
          )}

          <Tekstvak
            label="Toelichting"
            lezers="team"
            waarde={formulier.bewoner.toelichting}
            bijWijziging={(waarde) => zetBewoner('toelichting', waarde)}
            placeholder="Bijvoorbeeld: bewoner wil eerst overleggen met de buren."
            hint={<Schrijftip />}
          />

          {/* Zodra er íets persoonlijks staat moet het ook te wissen zijn.
              Een bewoner die alleen een telefoonnummer gaf, heeft evengoed
              recht om dat te laten verwijderen. */}
          {heeftPersoonsgegevens && (
            <Knop variant="subtiel" compact onClick={wisBewoner}>
              Persoonsgegevens wissen
            </Knop>
          )}
        </Sectie>

        <Sectie
          nummer={4}
          titel="Voortuin"
          ingevuld={tuinIngevuld(formulier.voortuin)}
          samenvatting={tuinSamenvatting(formulier.voortuin)}
        >
          <TuinVelden
            naam="Voortuin"
            waarde={formulier.voortuin}
            bijWijziging={(bijwerken) =>
              setFormulier((huidig) => ({ ...huidig, voortuin: bijwerken(huidig.voortuin) }))
            }
          />
          {fouten.bergingVoortuin && (
            <p className="veld__fout" role="alert">
              {fouten.bergingVoortuin}
            </p>
          )}
        </Sectie>

        <Sectie
          nummer={5}
          titel="Achtertuin"
          ingevuld={tuinIngevuld(formulier.achtertuin)}
          samenvatting={tuinSamenvatting(formulier.achtertuin)}
        >
          <TuinVelden
            naam="Achtertuin"
            waarde={formulier.achtertuin}
            bijWijziging={(bijwerken) =>
              setFormulier((huidig) => ({ ...huidig, achtertuin: bijwerken(huidig.achtertuin) }))
            }
          />
          {fouten.bergingAchtertuin && (
            <p className="veld__fout" role="alert">
              {fouten.bergingAchtertuin}
            </p>
          )}
        </Sectie>

        <Sectie
          nummer={6}
          titel="Resultaat"
          ingevuld={resultaatIngevuld}
          samenvatting={resultaatSamenvatting(formulier)}
        >
          {/*
            Twee vragen, en nooit een vraag die niet van de bewoner is.

            Bij een huurwoning is de regenpijp van de corporatie. Een huurder
            daarover laten antwoorden leverde precies de tegenspraak op waar
            de gemeente over viel: "gaat niet afkoppelen" bij een pand dat wel
            degelijk wordt afgekoppeld. Daarom staat het hier als feit en niet
            als keuze.
          */}
          {huur ? (
            <div className="veld">
              <span className="veld__label">Afkoppelen van het pand</span>
              <p className="sectie__feit">
                De corporatie koppelt dit pand af. Dat staat vast en hoeft hier niet
                ingevuld te worden.
              </p>
            </div>
          ) : (
            <>
              <TegelKiezer<Afkoppelbesluit>
                label="Afkoppelen van het pand"
                waarde={formulier.afkoppelbesluit}
                opties={AFKOPPELBESLUITEN}
                bijWijziging={(waarde) => zet('afkoppelbesluit', waarde)}
              />
              <p className="veld__hint">{LAAG_UITLEG.afkoppelen}</p>
            </>
          )}

          <TegelKiezer<Maatregelenbesluit>
            label="Extra maatregelen op het perceel"
            waarde={formulier.maatregelenbesluit}
            opties={MAATREGELENBESLUITEN}
            bijWijziging={(waarde) => zet('maatregelenbesluit', waarde)}
          />
          <p className="veld__hint">
            {LAAG_UITLEG.maatregelen} Wat je precies adviseert staat bij de voor- en
            achtertuin.
          </p>
          {/* De toets staat erbij omdat het grensgeval echt voorkomt: een
              infiltratiekrat waar de regenpijp op uitkomt is afkoppelen. */}
          <p className="veld__hint">{LAAG_TOETS}</p>

          <div className="veldenrij veldenrij--twee">
            <Keuzeveld<SubsidieStatus>
              label="Status subsidieaanvraag"
              waarde={formulier.subsidieStatus}
              opties={SUBSIDIE_STATUSSEN}
              bijWijziging={(waarde) => zet('subsidieStatus', waarde)}
            />
            <Keuzeveld<RealisatieStatus>
              label="Status realisatie afkoppeling"
              waarde={formulier.realisatieStatus}
              opties={REALISATIE_STATUSSEN}
              bijWijziging={(waarde) => zet('realisatieStatus', waarde)}
            />
          </div>

          <Tekstveld
            label="Datum vervolgafspraak"
            type="date"
            waarde={formulier.datumVervolg}
            bijWijziging={(waarde) => zet('datumVervolg', waarde)}
            hint="Alleen invullen als er iets is afgesproken."
          />

          {berging > 0 && (
            <p className="sectie__toelichting">
              Totale indicatieve berging: <strong>{berging.toFixed(1)} m³</strong> (voortuin en
              achtertuin samen).
            </p>
          )}

          <Tekstvak
            label="Algemene toelichting"
            lezers="gemeente"
            waarde={formulier.algemeneToelichting}
            bijWijziging={(waarde) => zet('algemeneToelichting', waarde)}
            rijen={4}
            hint={<Schrijftip />}
          />
        </Sectie>

        <Sectie
          nummer={7}
          titel="Foto's"
          ingevuld={formulier.fotos.length > 0}
          samenvatting={
            formulier.fotos.length > 0
              ? `${formulier.fotos.length} foto${formulier.fotos.length === 1 ? '' : "'s"}`
              : undefined
          }
        >
          <FotoSectie
            registratie={formulier}
            bijWijziging={(fotos: Foto[]) => zet('fotos', fotos)}
            bijFout={(bericht) => toonMelding('fout', bericht)}
          />
        </Sectie>

        <Sectie
          nummer={8}
          titel="Wijzigingen"
          ingevuld={formulier.versie > 1}
          samenvatting={
            formulier.versie > 1
              ? `${formulier.versie - 1} keer aangepast`
              : undefined
          }
        >
          <Historie registratie={formulier} />
        </Sectie>

        <div className="formulier__slot">
          <span className="formulier__statuslabel">Kleur op de kaart:</span>
          <StatusBadge status={afkoppelStatus(formulier, huur)} />
        </div>

        {bevestigVerwijderen ? (
          <div className="formulier__verwijderen">
            <p className="veld__fout">Weet je zeker dat je deze registratie wilt verwijderen?</p>
            <div className="fotoknoppen">
              <Knop variant="gevaar" onClick={() => void verwijderen(formulier)}>
                Ja, verwijderen
              </Knop>
              <Knop variant="secundair" onClick={() => setBevestigVerwijderen(false)}>
                Annuleren
              </Knop>
            </div>
          </div>
        ) : (
          <div className="formulier__verwijderen">
            <Knop
              variant="gevaar"
              icoon={<PrullenbakIcoon />}
              onClick={() => setBevestigVerwijderen(true)}
            >
              Registratie verwijderen
            </Knop>
          </div>
        )}
      </div>

      {vraagOverContact && (
        <div className="vraag-overlay" role="dialog" aria-modal="true">
          <div className="vraag">
            <h3>Was hier nieuw contact voor?</h3>
            <p>
              Je hebt het besluit, de status of het advies aangepast. Ging daar een gesprek,
              telefoontje of bericht aan vooraf, dan hoort dat in de tijdlijn. Was het een
              correctie op wat er al stond, dan hoeft dat niet.
            </p>
            <div className="vraag__velden">
              <Keuzeveld<ContactSoort>
                label="Wat voor contact was het?"
                waarde={nieuwContactSoort}
                opties={CONTACT_SOORTEN}
                bijWijziging={(waarde) =>
                  setNieuwContactSoort((waarde || 'Anders') as ContactSoort)
                }
                legeOptie="Kies..."
              />
              <Tekstveld
                label="Wanneer"
                type="date"
                waarde={nieuwContactDatum}
                bijWijziging={setNieuwContactDatum}
              />
            </div>

            <div className="vraag__knoppen">
              <Knop variant="primair" onClick={() => void bewaarMetContact()}>
                Toevoegen en opslaan
              </Knop>
              <Knop variant="secundair" onClick={() => void bewaarAlsCorrectie()}>
                Nee, dit was een correctie
              </Knop>
            </div>
          </div>
        </div>
      )}

      <div className="paneel__voet">
        <Knop variant="primair" icoon={<OpslaanIcoon />} bezig={bezig} onClick={() => void bewaar()}>
          Opslaan
        </Knop>
        <span className="autosave">
          {laatsteAutosave ? `Concept ${relatieveTijd(laatsteAutosave)}` : 'Concept actief'}
        </span>
      </div>
    </>
  );
}
